import React, { useEffect, useRef, useState } from 'react';
import { Button, Badge, Card, useToast } from '../../components/ui';
import { tasksApi } from '../../api/client';
import { useAppContext } from '../../store/AppContext';
import type { Task, TaskStatus } from '../../api/types';

interface Props {
  projectId: string;
}

const COLUMNS: { status: TaskStatus; label: string; color: string; headerBg: string; headerText: string }[] = [
  { status: 'TODO', label: 'To Do', color: '#6B7280', headerBg: '#F3F4F6', headerText: '#374151' },
  { status: 'IN_PROGRESS', label: 'In Progress', color: '#2563EB', headerBg: '#DBEAFE', headerText: '#1D4ED8' },
  { status: 'IN_REVIEW', label: 'In Review', color: '#D97706', headerBg: '#FEF3C7', headerText: '#92400E' },
  { status: 'DONE', label: 'Done', color: '#059669', headerBg: '#D1FAE5', headerText: '#065F46' },
];

export const TaskBoard: React.FC<Props> = ({ projectId }) => {
  const { tasks, setTasks, updateTask, addTask } = useAppContext();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    tasksApi.list(projectId).then((res) => {
      if (res.ok) setTasks(Array.isArray(res.data) ? res.data : ((res.data as { items?: Task[] }).items ?? []));
      setLoading(false);
    });
  }, [projectId, setTasks]);

  const handleMoveTask = async (taskId: string, newStatus: TaskStatus) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;
    // Optimistic update
    updateTask({ ...task, status: newStatus });
    const res = await tasksApi.updateStatus(taskId, newStatus);
    if (res.ok) {
      updateTask(res.data);
      const label = COLUMNS.find((c) => c.status === newStatus)?.label ?? newStatus;
      showToast(`Moved to ${label}`, 'success');
    } else {
      updateTask(task); // revert
      showToast('Failed to move task', 'error');
    }
  };

  const handleClaim = async (taskId: string) => {
    const res = await tasksApi.claim(taskId);
    if (res.ok) {
      updateTask(res.data);
      setSelectedTask(res.data);
      showToast('Task claimed!', 'success');
    } else {
      showToast(res.error?.message ?? 'Failed to claim task', 'error');
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    const res = await tasksApi.create(projectId, newTitle.trim(), newDesc.trim() || undefined);
    setCreating(false);
    if (res.ok) {
      addTask(res.data);
      setShowCreate(false);
      setNewTitle('');
      setNewDesc('');
      showToast('Task created', 'success');
    } else {
      showToast('Failed to create task', 'error');
    }
  };

  const handleDragStart = (taskId: string) => {
    setDraggedId(taskId);
  };

  const handleDrop = (status: TaskStatus) => {
    if (draggedId) {
      handleMoveTask(draggedId, status);
      setDraggedId(null);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--text-muted)',
        }}
      >
        Loading tasks…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'white',
          flexShrink: 0,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Task Board</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + New Task
        </Button>
      </div>

      {/* Kanban board */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 20px',
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          background: 'var(--bg-secondary)',
        }}
      >
        {COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col.status);
          return (
            <KanbanColumn
              key={col.status}
              column={col}
              tasks={colTasks}
              onTaskClick={setSelectedTask}
              onDrop={() => handleDrop(col.status)}
              onDragStart={handleDragStart}
            />
          );
        })}
      </div>

      {/* Task detail modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onClaim={() => handleClaim(selectedTask.id)}
          onMove={(status) => {
            handleMoveTask(selectedTask.id, status);
            setSelectedTask({ ...selectedTask, status });
          }}
        />
      )}

      {/* Create task modal */}
      {showCreate && (
        <CreateTaskModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreateTask}
          title={newTitle}
          setTitle={setNewTitle}
          desc={newDesc}
          setDesc={setNewDesc}
          creating={creating}
        />
      )}
    </div>
  );
};

function KanbanColumn({
  column,
  tasks,
  onTaskClick,
  onDrop,
  onDragStart,
}: {
  column: { status: TaskStatus; label: string; color: string; headerBg: string; headerText: string };
  tasks: Task[];
  onTaskClick: (t: Task) => void;
  onDrop: () => void;
  onDragStart: (id: string) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={() => { onDrop(); setIsDragOver(false); }}
    >
      {/* Column header — colored like prototype */}
      <div style={{
        padding: '8px 12px',
        borderRadius: '8px 8px 0 0',
        background: isDragOver ? '#EEF2FF' : column.headerBg,
        color: isDragOver ? 'var(--primary)' : column.headerText,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 0,
        transition: 'all 0.15s',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{column.label}</span>
        <span style={{
          fontSize: 11, fontWeight: 600,
          background: 'rgba(0,0,0,0.08)',
          padding: '1px 7px', borderRadius: 10,
        }}>{tasks.length}</span>
      </div>

      {/* Task cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60 }}>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onClick={() => onTaskClick(task)}
            onDragStart={() => onDragStart(task.id)}
          />
        ))}
        {tasks.length === 0 && (
          <div style={{
            padding: '20px 10px', fontSize: 12,
            color: 'var(--text-muted)', textAlign: 'center',
            background: isDragOver ? '#EEF2FF' : 'var(--bg-secondary)',
            borderRadius: 'var(--radius)',
            border: isDragOver ? '2px dashed var(--primary)' : '1px dashed var(--border)',
          }}>
            {isDragOver ? '⬇ Drop here' : 'No tasks'}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({
  task,
  onClick,
  onDragStart,
}: {
  task: Task;
  onClick: () => void;
  onDragStart: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'all 0.12s',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'var(--primary)';
        el.style.boxShadow = '0 2px 8px rgba(79,70,229,0.12)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'var(--border)';
        el.style.boxShadow = 'none';
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, lineHeight: 1.4 }}>
        {task.title}
      </div>
      {task.description && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            marginBottom: 8,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {task.description}
        </div>
      )}
      {task.assignee && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: '#7C3AED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              color: 'white',
            }}
          >
            {task.assignee.displayName.slice(0, 2).toUpperCase()}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {task.assignee.displayName}
          </span>
        </div>
      )}
    </div>
  );
}

function TaskDetailModal({
  task,
  onClose,
  onClaim,
  onMove,
}: {
  task: Task;
  onClose: () => void;
  onClaim: () => void;
  onMove: (s: TaskStatus) => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const statusVariantMap: Record<TaskStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
    TODO: 'neutral',
    IN_PROGRESS: 'info',
    IN_REVIEW: 'warning',
    DONE: 'success',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 28,
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, flex: 1, lineHeight: 1.4 }}>
            {task.title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: 'var(--text-muted)',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Badge variant={statusVariantMap[task.status]}>{task.status.replace('_', ' ')}</Badge>
          {task.assignee && (
            <Badge variant="neutral">Assigned to {task.assignee.displayName}</Badge>
          )}
        </div>

        {task.description && (
          <p
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              marginBottom: 20,
            }}
          >
            {task.description}
          </p>
        )}

        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-muted)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Move to
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {COLUMNS.filter((c) => c.status !== task.status).map((c) => (
              <button
                key={c.status}
                onClick={() => onMove(c.status)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  border: `1px solid ${c.color}`,
                  background: 'white',
                  color: c.color,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {!task.claimedBy && (
            <Button onClick={onClaim}>Claim task</Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateTaskModal({
  onClose,
  onCreate,
  title,
  setTitle,
  desc,
  setDesc,
  creating,
}: {
  onClose: () => void;
  onCreate: (e: React.FormEvent) => void;
  title: string;
  setTitle: (v: string) => void;
  desc: string;
  setDesc: (v: string) => void;
  creating: boolean;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 28,
          width: '100%',
          maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>New Task</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: 'var(--text-muted)',
            }}
          >
            ✕
          </button>
        </div>
        <form onSubmit={onCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Task title"
              required
              autoFocus
              style={{
                padding: '9px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                fontSize: 14,
                outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Description (optional)</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What needs to be done?"
              rows={3}
              style={{
                padding: '9px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                fontSize: 13,
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating || !title.trim()}>
              {creating ? 'Creating…' : 'Create task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
